<script setup lang="ts">

import {computed, reactive, ref, watch} from "vue";
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from "@/core/components/ui/dialog";
import {Input} from "@/core/components/ui/input";
import {Textarea} from "@/core/components/ui/textarea";
import {Label} from "@/core/components/ui/label";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {referralApi} from "@/container";
import {ReferralRequestType} from "@/core/http/resources/referral/ReferralResource";
import {useToastStore} from "@/stores/toast.store";

const {type} = defineProps<{
    type: ReferralRequestType;
}>();

const {showSuccess, showError} = useToastStore();

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^\+?[\d .()-]{6,20}$/;

const open = ref(false);
const loading = ref(false);
const establishment = ref('');
const contactEmail = ref('');
const contactPhone = ref('');
const comment = ref('');
const errors = reactive<{
    establishment?: string;
    contactEmail?: string;
    contactPhone?: string;
}>({});

const errorClass = 'border-red-400 ring-1 ring-red-400 focus-visible:ring-red-400';

const title = computed(() => {
    return type === 'hospital'
        ? 'Référencez un nouveau centre hospitalier'
        : 'Référencez un nouveau laboratoire';
});

watch(establishment, () => errors.establishment = undefined);
watch(contactEmail, () => errors.contactEmail = undefined);
watch(contactPhone, () => errors.contactPhone = undefined);

/**
 * Validate the form fields, filling the errors state.
 */
const validate = (): boolean => {
    errors.establishment = establishment.value.trim()
        ? undefined
        : 'Veuillez renseigner l’établissement.';

    if (!contactEmail.value.trim()) {
        errors.contactEmail = 'Veuillez renseigner l’email du contact.';
    } else if (!EMAIL_PATTERN.test(contactEmail.value.trim())) {
        errors.contactEmail = 'Cette adresse email n’est pas valide.';
    } else {
        errors.contactEmail = undefined;
    }

    if (!contactPhone.value.trim()) {
        errors.contactPhone = 'Veuillez renseigner le numéro de téléphone du contact.';
    } else if (!PHONE_PATTERN.test(contactPhone.value.trim())) {
        errors.contactPhone = 'Ce numéro de téléphone n’est pas valide.';
    } else {
        errors.contactPhone = undefined;
    }

    return !errors.establishment && !errors.contactEmail && !errors.contactPhone;
};

/**
 * Reset the form fields and errors.
 */
const reset = () => {
    establishment.value = '';
    contactEmail.value = '';
    contactPhone.value = '';
    comment.value = '';
    errors.establishment = undefined;
    errors.contactEmail = undefined;
    errors.contactPhone = undefined;
};

/**
 * Send the referral request, then close the dialog and reset the form.
 */
const send = () => {
    if (!validate()) {
        return;
    }

    loading.value = true;

    referralApi()
        .send({
            type,
            establishment: establishment.value.trim(),
            contact_email: contactEmail.value.trim(),
            contact_phone: contactPhone.value.trim(),
            comment: comment.value || undefined
        })
        .then(() => {
            showSuccess({content: 'Votre demande de référencement a bien été envoyée.'});
            open.value = false;
            reset();
        })
        .catch(() => {
            showError({content: 'Une erreur est survenue lors de l’envoi de votre demande.'});
        })
        .finally(() => {
            loading.value = false;
        });
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger as-child>
      <slot />
    </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          {{ title }}
        </DialogTitle>
      </DialogHeader>
      <form class="flex flex-col gap-4" novalidate @submit.prevent="send">
        <div class="flex flex-col gap-1.5">
          <Label for="referral-establishment">Établissement :</Label>
          <Input
            id="referral-establishment"
            v-model="establishment"
            :class="errors.establishment && errorClass"
            :aria-invalid="!!errors.establishment"
          />
          <p v-if="errors.establishment" class="text-xs text-red-500">
            {{ errors.establishment }}
          </p>
        </div>
        <div class="flex flex-col gap-1.5">
          <Label for="referral-contact-email">Email du contact :</Label>
          <Input
            id="referral-contact-email"
            v-model="contactEmail"
            type="email"
            :class="errors.contactEmail && errorClass"
            :aria-invalid="!!errors.contactEmail"
          />
          <p v-if="errors.contactEmail" class="text-xs text-red-500">
            {{ errors.contactEmail }}
          </p>
        </div>
        <div class="flex flex-col gap-1.5">
          <Label for="referral-contact-phone">Numéro de téléphone du contact :</Label>
          <Input
            id="referral-contact-phone"
            v-model="contactPhone"
            type="tel"
            :class="errors.contactPhone && errorClass"
            :aria-invalid="!!errors.contactPhone"
          />
          <p v-if="errors.contactPhone" class="text-xs text-red-500">
            {{ errors.contactPhone }}
          </p>
        </div>
        <div class="flex flex-col gap-1.5">
          <Label for="referral-comment">Avez-vous un commentaire à joindre à votre demande ?</Label>
          <Textarea
            id="referral-comment"
            v-model="comment"
          />
        </div>
        <BaseButton
          primary
          type="submit"
          class="self-end"
          :loading="loading"
          :disabled="loading"
        >
          Envoyer la demande de référencement
        </BaseButton>
      </form>
    </DialogContent>
  </Dialog>
</template>

<style scoped>

</style>
